import type fr from '../fr/adminHr';

const en: typeof fr = {
  labelColon: '{{label}}:',
  employeeStatus: { ACTIVE: 'Active', INVITED: 'Invited', ON_LEAVE: 'On leave', INACTIVE: 'Inactive' },
  skills: { STANDARD: 'Standard', DEEP: 'Deep cleaning', MOVE: 'Move in/out', COMMERCIAL: 'Commercial', PETS_OK: 'Pets OK' },

  employees: {
    title: 'Employees',
    subtitle: 'Field staff, skills, sectors and today’s workload.',
    new: 'New employee',
    search: 'Search by name, email or phone',
    allStatuses: 'All statuses',
    empty: 'No employees yet.',
    emptyFiltered: 'No employee matches these filters.',
    name: 'Name',
    skills: 'Skills',
    sectors: 'Sectors',
    teams: 'Teams',
    today: 'Today',
    jobsToday_one: '{{count}} job today',
    jobsToday_other: '{{count}} jobs today',
  },

  form: {
    newTitle: 'New employee',
    newSubtitle: 'An employee account is created with a temporary password to share.',
    editTitle: 'Edit {{name}}',
    editTitleShort: 'Edit employee',
    identity: 'Identity',
    fullName: 'Full name',
    emailHint: 'Used as the login.',
    statusHint: 'Status is changed later from the profile.',
    hireDate: 'Hire date',
    color: 'Colour',
    colorHint: 'Used on the schedule and the map.',
    work: 'Work',
    noSectors: 'No sectors configured (Settings > Sectors).',
    dailyCapacity: 'Max jobs per day',
    dailyCapacityHint: 'Empty = company default.',
    maxHours: 'Max hours per day',
    maxHoursHint: 'Empty = company default.',
    hasVehicle: 'Has a vehicle',
    vehicleNotes: 'Vehicle notes',
    home: 'Home',
    homeHint: 'Starting point for distance estimates (never shown to clients).',
    lat: 'Latitude',
    lng: 'Longitude',
    internalNotes: 'Internal notes',
    internalNotesHint: 'Visible to staff only.',
    createAndInvite: 'Create and invite',
    created: 'Employee created.',
    saved: 'Profile saved.',
    invalid: 'Invalid value.',
  },

  invite: {
    title: 'Invitation',
    description: 'Share these sign-in details with the person.',
    email: 'Login: {{email}}',
    once: 'This temporary password will not be shown again. The person can change it after signing in.',
    copied: 'Password copied.',
    done: 'Got it',
    selfSignup: 'The account is ready. The person sets their own password.',
    selfSignupSteps: 'Ask them to sign up at {{url}} with this same email and confirm the link they receive. Their account will be linked automatically.',
  },

  availability: {
    title: 'Weekly availability',
    off: 'Not available',
    start: 'Start',
    end: 'End',
    add: 'Slot',
    remove: 'Remove slot',
    invalid: 'Each slot must end after it starts.',
  },

  comp: {
    title: 'Compensation',
    edit: 'Change compensation',
    editDialogHint: 'The new rule applies from today. History is kept.',
    createHint: 'Rule applied to completed jobs. Can be changed later from the profile.',
    editHint: 'If you change the rule, a new rule starts today (history is kept).',
    method: 'Calculation',
    fixed: 'Amount per job',
    hourly: 'Hourly rate',
    percentage: 'Share of client price',
    percentageHint: 'Split between the employees on the job.',
    perHour: '{{amount}}/h',
    none: 'No rule: company defaults apply.',
    since: 'Since {{from}}',
    range: 'From {{from}} to {{to}}',
    serviceSpecific: 'specific service',
    saved: 'Compensation updated.',
  },

  detail: {
    profile: 'Profile',
    vehicle: 'Vehicle',
    capacity: 'Capacity',
    capacityJobs: '{{count}} jobs max per day',
    capacityHours: '{{count}} h max per day',
    capacityDefault: 'Company defaults',
    lastLogin: 'Last sign-in',
    never: 'Never',
    sectorsTeams: 'Sectors and teams',
    upcoming: 'Upcoming jobs',
    upcomingCount: 'Upcoming',
    next30: 'Next 30 days',
    noUpcoming: 'No jobs scheduled.',
    history: 'Recent history',
    noHistory: 'No past jobs.',
    jobsDone: 'Jobs done',
    timeWorked: 'Time worked',
    recentHistory: 'Last 20 jobs',
    payroll: 'Payroll',
    changeStatus: 'Status',
    setStatus: { ACTIVE: 'Mark active', ON_LEAVE: 'Put on leave', INACTIVE: 'Deactivate' },
    statusChanged: 'Status updated.',
    deactivate: 'Deactivate',
    deactivateTitle: 'Deactivate {{name}}?',
    deactivateBody: 'They will no longer be able to sign in. Their upcoming jobs will be removed from them and go back to dispatch to be reassigned.',
    deactivatedTitle: 'Employee deactivated',
    releasedJobs_one: '{{count}} upcoming job went back to dispatch.',
    releasedJobs_other: '{{count}} upcoming jobs went back to dispatch.',
    openDispatch: 'Open schedule',
  },

  timeOff: {
    title: 'Time off',
    add: 'Add time off',
    addHint: 'The employee will not get jobs on these dates.',
    empty: 'No upcoming time off.',
    start: 'From',
    end: 'To',
    reason: 'Reason (optional)',
    range: 'From {{from}} to {{to}}',
    invalid: 'End must be after start.',
    added: 'Time off added.',
    remove: 'Remove time off',
    removeTitle: 'Remove this time off?',
    removed: 'Time off removed.',
  },

  teams: {
    title: 'Teams',
    subtitle: 'Groups of employees who work together, with their sectors and capacity.',
    new: 'New team',
    edit: 'Edit team',
    empty: 'No teams yet.',
    name: 'Name',
    namePlaceholder: 'Team Alpha',
    members: 'Members',
    leader: 'Team leader',
    noLeader: 'No leader',
    noMembers: 'No members',
    capacity: 'Max jobs per day',
    capacityHint: 'Empty = company default.',
    capacityValue_one: '{{count}} job max per day',
    capacityValue_other: '{{count}} jobs max per day',
    capacityDefault: 'Company default capacity',
    active: 'Active team',
    inactive: 'Inactive',
    deactivate: 'Deactivate',
    deactivateTitle: 'Deactivate {{name}}?',
    deactivateBody: 'The team will no longer be suggested in dispatch. Employees stay active and already assigned jobs do not change.',
    deactivated: 'Team deactivated.',
    created: 'Team created.',
    saved: 'Team saved.',
  },

  payroll: {
    title: 'Payroll',
    notice: 'The platform computes and tracks what is owed. Approved pay can be sent to self-employed workers below; “Mark paid” records a payment made outside the app.',
    range: 'Period',
    ranges: { today: 'Today', week: 'This week', period: 'Pay period', custom: 'Custom' },
    rangeLabel: 'From {{from}} to {{to}}',
    from: 'From',
    to: 'To',
    allEmployees: 'All employees',
    totals: { pendingCents: 'Pending', approvedCents: 'Approved', paidCents: 'Paid' },
    byEmployee: 'By employee',
    jobsCount_one: '{{count}} job',
    jobsCount_other: '{{count}} jobs',
    pendingShort: '{{amount}} pending',
    entries: 'Payroll lines',
    emptyEntries: 'No payroll lines for this period.',
    emptyHint: 'Lines are created automatically when a job is completed.',
    job: 'Job',
    method: 'Calculation',
    selectAll: 'Select all',
    unselectAll: 'Unselect all',
    selectEntry: 'Select line',
    selected_one: '{{count}} line selected · {{amount}}',
    selected_other: '{{count}} lines selected · {{amount}}',
    mixedHint: 'The selection mixes statuses: approve pending lines and mark approved lines as paid separately.',
    actions: { APPROVE: 'Approve', MARK_PAID: 'Mark paid', VOID: 'Void' },
    confirm: {
      APPROVE: {
        title_one: 'Approve {{count}} line?',
        title_other: 'Approve {{count}} lines?',
        body_one: 'Total: {{amount}}. It can then be marked as paid.',
        body_other: 'Total: {{amount}}. They can then be marked as paid.',
      },
      MARK_PAID: {
        title_one: 'Mark {{count}} line as paid?',
        title_other: 'Mark {{count}} lines as paid?',
        body_one: 'Total: {{amount}}. Only confirm if the employee was paid outside the app (no transfer is made here).',
        body_other: 'Total: {{amount}}. Only confirm if the employees were paid outside the app (no transfer is made here).',
      },
      VOID: {
        title_one: 'Void {{count}} line?',
        title_other: 'Void {{count}} lines?',
        body_one: 'Total: {{amount}}. It will no longer be owed. This is kept in the audit log.',
        body_other: 'Total: {{amount}}. They will no longer be owed. This is kept in the audit log.',
      },
    },
    voidReason: 'Reason',
    actionDone_one: '{{count}} line updated.',
    actionDone_other: '{{count}} lines updated.',
  },

  finances: {
    title: 'Finances',
    actionsColumn: 'Actions',
    subtitle: 'Sales, payments and employee costs for the month.',
    notice: 'Simplified management view to follow the business. This is not certified accounting: ask your accountant for financial statements and taxes.',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    expectedSales: 'Expected sales',
    completedSales: 'Completed sales',
    paymentsReceived: 'Payments received',
    employeeCosts: 'Employee costs',
    grossMargin: 'Operating gross margin',
    failedPayments: 'Failed payments',
    jobsScheduled_one: '{{count}} job scheduled',
    jobsScheduled_other: '{{count}} jobs scheduled',
    jobsCompleted_one: '{{count}} job completed',
    jobsCompleted_other: '{{count}} jobs completed',
    paymentsCount_one: '{{count}} payment',
    paymentsCount_other: '{{count}} payments',
    payrollPending: 'including {{amount}} to approve',
    marginRatio: '{{pct}}% of completed sales',
    marginHint: 'Completed sales − employee costs',
    failedCount_one: '{{count}} payment to follow up',
    failedCount_other: '{{count}} payments to follow up',
    cancelled_one: '{{count}} job cancelled this month.',
    cancelled_other: '{{count}} jobs cancelled this month.',
    daily: 'Per day: expected and completed',
    noDaily: 'No jobs this month.',
    expected: 'Expected',
    completed: 'Completed',
    barLabel: 'Completed {{completed}} of {{expected}} expected',
    payments: 'Client payments',
    paymentTabs: { attention: 'Failed and pending', paid: 'Paid' },
    noProblemPayments: 'No failed or pending payments.',
    noPaidPayments: 'No payments received.',
    reference: 'Reference',
    refundedAmount: '{{amount}} refunded',
    recordRefund: 'Record a refund',
    refundTitle: 'Record a refund',
    refundBody: 'Client: {{client}}. Refundable amount: {{amount}}.',
    refundAmount: 'Amount refunded',
    refundHint: 'Records the refund in the app (made with the payment provider).',
    refundInvalid: 'Between $0.01 and {{max}}.',
    refunded: 'Refund recorded.',
  },

  permissionGroups: {
    clients: 'Clients',
    properties: 'Properties',
    quotes: 'Quotes',
    services: 'Services',
    jobs: 'Jobs and dispatch',
    employees: 'Employees and teams',
    payroll: 'Payroll',
    payments: 'Payments and finances',
    chat: 'Messages',
    admin: 'Administration',
  },
  permissions: {
    'clients.read': 'View clients',
    'clients.write': 'Edit clients',
    'properties.read': 'View properties',
    'properties.write': 'Edit properties',
    'properties.sensitive.read': 'View access info (codes, keys)',
    'quotes.read': 'View quotes',
    'quotes.write': 'Prepare quotes',
    'quotes.approve': 'Approve prices',
    'services.read': 'View services',
    'services.manage': 'Manage services and checklists',
    'jobs.read': 'View schedule',
    'jobs.write': 'Create and edit jobs',
    'jobs.assign': 'Assign employees',
    'jobs.override': 'Force an assignment (override)',
    'employees.read': 'View employees',
    'employees.manage': 'Manage employees',
    'teams.read': 'View teams',
    'teams.manage': 'Manage teams',
    'payroll.read': 'View payroll',
    'payroll.approve': 'Approve payroll and compensation',
    'payments.read': 'View payments',
    'payments.manage': 'Manage payments (refunds)',
    'chat.read': 'Read messages',
    'chat.write': 'Reply to messages',
    'chat.assign': 'Assign conversations',
    'finance.read': 'View finances',
    'analytics.read': 'View statistics',
    'map.read': 'View map',
    'settings.read': 'View settings',
    'settings.manage': 'Edit settings',
    'users.manage': 'Manage users',
    'owners.manage': 'Manage owners',
    'audit.read': 'View audit log',
  },

  settings: {
    title: 'Settings',
    subtitle: 'Company, scheduling, pricing, payroll and access.',
    tabs: {
      company: 'Company',
      planning: 'Scheduling',
      pricing: 'Pricing',
      payroll: 'Payroll',
      notifications: 'Notifications',
      sectors: 'Sectors',
      services: 'Services and checklists',
      users: 'Users and permissions',
      emails: 'Emails',
      audit: 'Audit log',
    },
    readOnly: 'Read only: you do not have permission to change these settings.',
    saved: 'Settings saved.',
    unsaved: 'Unsaved changes',
    invalid: 'Some values are invalid.',
    nameFr: 'Name (French)',
    nameEn: 'Name (English)',
    labelFr: 'Label (French)',
    labelEn: 'Label (English)',
    descriptionFr: 'Description (French)',
    descriptionEn: 'Description (English)',
    company: {
      title: 'Company',
      description: 'Contact details shown to clients and in emails.',
      name: 'Company name',
      timezone: 'Time zone',
      timezoneHint: 'All times are shown in this time zone (America/Toronto by default).',
      currency: 'Currency',
      language: 'Default language',
    },
    planning: {
      hoursTitle: 'Business hours',
      openingTime: 'Opening',
      closingTime: 'Closing',
      workingDays: 'Working days',
      bufferMinutes: 'Buffer between jobs (minutes)',
      bufferMinutesHint: 'Minimum travel time added between two jobs.',
      capacityTitle: 'Capacity and distances',
      maxJobsPerEmployeePerDay: 'Max jobs per employee per day',
      maxJobsPerEmployeePerDayHint: 'Can be adjusted on each employee.',
      maxJobsPerTeamPerDay: 'Max jobs per team per day',
      maxJobsPerTeamPerDayHint: 'Can be adjusted on each team.',
      maxHoursPerDay: 'Max hours per employee per day',
      maxHoursPerDayHint: 'Beyond that, dispatch flags a conflict.',
      maxDistanceKm: 'Max distance from home (km)',
      maxDistanceKmHint: 'Employees farther away rank lower.',
      cutoffTitle: 'Client change deadline',
      cutoffDescription: 'After this deadline, clients can no longer change or cancel by themselves: they must write to the team.',
      cutoffMode: 'Rule',
      cutoffModes: { HOURS: 'Hours before the job', BUSINESS_DAY: 'Business days before the job' },
      cutoffHours: 'Hours before',
      hoursValue_one: '{{count}} hour',
      hoursValue_other: '{{count}} hours',
      cutoffBusinessDays: 'Business days before',
      cutoffBusinessDaysHint: '1 = no later than the previous business day.',
      recurrenceTitle: 'Recurring jobs',
      recurrenceHorizonDays: 'Generation horizon (days)',
      recurrenceHorizonDaysHint: 'How many days ahead recurring jobs are created on the schedule.',
    },
    pricing: {
      notice: 'These rules produce an internal estimate. The official price of a quote is always set by the team. Amounts in dollars.',
      baseTitle: 'Base price',
      baseDescription: 'By property type.',
      roomsTitle: 'Per-room prices and surcharges',
      perBedroom: 'Per bedroom',
      perBathroom: 'Per bathroom',
      perKitchen: 'Per kitchen',
      perFamilyRoom: 'Per family room',
      perExtraFloor: 'Per extra floor',
      petSurcharge: 'Pet surcharge',
      sqftTitle: 'Square footage',
      sqftDescription: 'Surcharge per square foot above the threshold.',
      sqftThreshold: 'Threshold (sq ft)',
      perSqftAbove: 'Per sq ft above',
      discountsTitle: 'Frequency discounts',
      discountsDescription: 'Percentage taken off the estimated price.',
      extrasTitle: 'Extras',
      extrasDescription: 'Options clients can add (extra price and time).',
      extraCode: 'Code',
      extraMinutes: 'Minutes',
      addExtra: 'Add an extra',
      removeExtra: 'Remove extra',
      minutesTitle: 'Estimated duration (minutes)',
      minutesDescription: 'Used to estimate duration and hourly pay.',
      minutes: {
        base: 'Base',
        perBedroom: 'Per bedroom',
        perBathroom: 'Per bathroom',
        perKitchen: 'Per kitchen',
        perFamilyRoom: 'Per family room',
        perExtraFloor: 'Per extra floor',
        pets: 'Pets',
      },
      rulesTitle: 'General rules',
      minimumPrice: 'Minimum price',
      rounding: 'Round to a multiple of',
      secondWorker: 'Second worker above (minutes)',
      secondWorkerHint: 'Above this duration, two workers are recommended.',
    },
    payroll: {
      periodTitle: 'Pay period',
      periodDescription: 'Groups payroll lines and drives the “Pay period” filter.',
      periodKind: 'Frequency',
      kinds: { WEEKLY: 'Weekly', BIWEEKLY: 'Every two weeks', SEMI_MONTHLY: 'Twice a month (1st and 16th)', MONTHLY: 'Monthly' },
      anchor: 'Reference start date',
      anchorHint: 'First day of a period (weekly or every two weeks).',
      defaultsTitle: 'Default compensation',
      defaultsDescription: 'Applied to employees without a personal rule.',
    },
    notifications: {
      title: 'Notifications',
      description: 'Emails sent to clients and employees.',
      emailEnabled: 'Send emails',
      reminderEnabled: 'Reminder before each job',
      reminderHoursBefore: 'Hours before the job',
      employeeDailySummary: 'Daily summary to employees',
    },
    sectors: {
      description: 'Service areas (e.g. South Shore) and their sectors (e.g. Saint-Hubert). A property’s sector is detected from the centre and radius.',
      empty: 'No service areas.',
      newArea: 'New area',
      areaHint: 'Large region grouping several sectors.',
      areaPlaceholder: 'South Shore',
      areaCreated: 'Area created.',
      addSector: 'Sector',
      newSector: 'New sector',
      editSector: 'Edit sector',
      noSectors: 'No sectors in this area.',
      area: 'Area',
      name: 'Name',
      areaDescription: 'Description',
      radius: 'Radius (km)',
      radiusValue: '{{km}} km radius',
      centerHint: 'Centre and radius are used to detect an address’s sector automatically.',
      active: 'Active sector',
      inactive: 'Inactive',
      properties_one: '{{count}} property',
      properties_other: '{{count}} properties',
      saved: 'Sector saved.',
      deleted: 'Sector deleted.',
      deleteTitle: 'Delete sector {{name}}?',
      deleteBody: 'The sector will be removed from employees and teams.',
      deleteInUse_one: 'This sector still has {{count}} property: move it first or deactivate the sector.',
      deleteInUse_other: 'This sector still has {{count}} properties: move them first or deactivate the sector.',
    },
    services: {
      readOnly: 'Read only: managing services requires the “Manage services and checklists” permission.',
      servicesTitle: 'Service types',
      editService: 'Edit service {{code}}',
      priceMultiplier: 'Price multiplier',
      durationMultiplier: 'Duration multiplier',
      multiplierHint: '1 = standard price, 1.5 = 50% more.',
      multipliers: 'Price ×{{price}} · Duration ×{{duration}}',
      defaultTemplate: 'Default checklist',
      active: 'Service offered',
      inactive: 'Not offered',
      saved: 'Service saved.',
      templatesTitle: 'Checklists',
      newTemplate: 'New checklist',
      editTemplate: 'Edit checklist',
      noTemplates: 'No checklists.',
      code: 'Code',
      codeHint: 'Capitals, digits and _ only.',
      templateDescription: 'Description',
      addTask: 'Task',
      required: 'Required',
      optional: 'optional',
      itemsInvalid: 'Each task needs a French and an English label.',
      taskCount_one: '{{count}} task',
      taskCount_other: '{{count}} tasks',
      templateSaved: 'Checklist saved.',
    },
    users: {
      staffTitle: 'Office staff',
      new: 'New user',
      newHint: 'Administrator or supervisor. A temporary password will be shown once.',
      invite: 'Create and invite',
      created: 'User created.',
      role: 'Role',
      you: 'You',
      inactive: 'Deactivated',
      lastLogin: 'Last sign-in: {{date}}',
      neverLoggedIn: 'Never signed in',
      overridesCount: '{{granted}} added, {{revoked}} removed',
      permissions: 'Permissions',
      overridesTitle: 'Permissions for {{name}}',
      overridesHint: 'By default, this person has the {{role}} role permissions. Add or remove permissions for them only.',
      defaultYes: 'Default (yes)',
      defaultNo: 'Default (no)',
      grant: 'Grant',
      revoke: 'Remove',
      permissionsSaved: 'Permissions saved.',
      activate: 'Reactivate',
      deactivate: 'Deactivate',
      deactivateTitle: 'Deactivate {{name}}?',
      deactivateBody: 'They will no longer be able to sign in. You can reactivate them later.',
      activeSaved: 'Access updated.',
    },
    roles: {
      title: 'Permissions by role',
      description: 'Supervisors use the same interface as administrators: what they cannot do disappears.',
      ownerHint: 'The owner always has every permission.',
      notEditable: 'Only an owner can change this role’s permissions.',
      saved: 'Role permissions saved.',
    },
    outbox: {
      description: 'Emails generated by the app (development provider: nothing is actually sent, the content is visible here).',
      refresh: 'Refresh',
      empty: 'No emails yet.',
      scheduledFor: 'Scheduled: {{date}}',
      sentAt: 'Sent: {{date}}',
      error: 'Failed after {{attempts}} attempt(s): {{error}}',
      notRendered: 'Content not generated yet.',
      status: { PENDING: 'Pending', PROCESSING: 'Processing', SENT: 'Sent', FAILED: 'Failed', CANCELLED: 'Cancelled' },
      types: {
        CLIENT_ACCOUNT_CREATED: 'Client account created',
        QUOTE_AVAILABLE: 'Quote available',
        QUOTE_ACCEPTED: 'Quote accepted',
        BOOKING_CONFIRMED: 'Booking confirmed',
        BOOKING_CHANGED: 'Booking changed',
        BOOKING_CANCELLED: 'Booking cancelled',
        JOB_REMINDER: 'Job reminder',
        JOB_COMPLETED: 'Job completed',
        PAYMENT_RECEIVED: 'Payment received',
        PAYMENT_FAILED: 'Payment failed',
        EMPLOYEE_JOB_ASSIGNED: 'Job assigned (employee)',
        EMPLOYEE_SCHEDULE_CHANGED: 'Schedule changed (employee)',
        EMPLOYEE_JOB_CANCELLED: 'Job cancelled (employee)',
        EMPLOYEE_DAILY_SUMMARY: 'Daily summary (employee)',
        NEW_MESSAGE: 'New message',
      },
    },
    audit: {
      description: 'Who did what, and when. Sensitive changes (prices, payroll, permissions, statuses) are kept here.',
      allEntities: 'All items',
      entity: 'Item',
      actor: 'By',
      action: 'Action',
      change: 'Before → after',
      system: 'System',
      empty: 'No entries.',
      entities: {
        employee: 'Employee',
        team: 'Team',
        payroll_entry: 'Payroll',
        job: 'Job',
        quote: 'Quote',
        agreement: 'Agreement',
        client: 'Client',
        property: 'Property',
        payment: 'Payment',
        conversation: 'Conversation',
        business_settings: 'Settings',
        sector: 'Sector',
        service_area: 'Service area',
        service_type: 'Service type',
        task_template: 'Checklist',
        user: 'User',
        role: 'Role',
      },
    },
  },
};
export default en;
